import { useMemo, useState } from 'react';
import {
  ACCEPT_PRESETS, DEFAULT_ACCEPT, MAX_FILES_CEILING, MAX_SIZE_MB_CEILING, UploadLimits, normalizeAccept,
} from '../../form-engine/uploadLimits';

/**
 * The three upload limits as they are edited: on Settings for the whole form,
 * and in the builder's file-field panel as an override. Numbers are strings
 * so an input can sit empty ("inherit"); the accept list is a preset or a row
 * of chips. `inherited` is what applies while a control is blank, shown as
 * the placeholder so nobody has to remember the defaults.
 */
export interface UploadLimitsValue {
  maxFiles: string;
  maxSizeMb: string;
  accept: string[];
}

export const emptyUploadLimits = (): UploadLimitsValue => ({ maxFiles: '', maxSizeMb: '', accept: [] });

/** The stored shape from the edited one; undefined when nothing is set. */
export function uploadLimitsToMeta(v: UploadLimitsValue): { maxFiles?: number; maxSizeMb?: number; accept?: string[] } | undefined {
  const out: { maxFiles?: number; maxSizeMb?: number; accept?: string[] } = {};
  const files = Math.floor(Number(v.maxFiles.trim()));
  if (v.maxFiles.trim() && Number.isFinite(files) && files >= 1) out.maxFiles = Math.min(MAX_FILES_CEILING, files);
  const mb = Math.floor(Number(v.maxSizeMb.trim()));
  if (v.maxSizeMb.trim() && Number.isFinite(mb) && mb >= 1) out.maxSizeMb = Math.min(MAX_SIZE_MB_CEILING, mb);
  const accept = normalizeAccept(v.accept);
  if (accept.length) out.accept = accept;
  return Object.keys(out).length ? out : undefined;
}

/** The edited shape from the stored one. */
export function uploadLimitsFromMeta(m?: { maxFiles?: number; maxSizeMb?: number; accept?: string[] } | null): UploadLimitsValue {
  return {
    maxFiles: m?.maxFiles ? String(m.maxFiles) : '',
    maxSizeMb: m?.maxSizeMb ? String(m.maxSizeMb) : '',
    accept: normalizeAccept(m?.accept),
  };
}

type Preset = 'inherit' | 'documents' | 'images' | 'custom';

const sameList = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x, i) => x === b[i]);

function presetOf(accept: string[]): Preset {
  if (!accept.length) return 'inherit';
  if (sameList(accept, ACCEPT_PRESETS.documents)) return 'documents';
  if (sameList(accept, ACCEPT_PRESETS.images)) return 'images';
  return 'custom';
}

export interface UploadLimitsFieldsProps {
  value: UploadLimitsValue;
  onChange: (v: UploadLimitsValue) => void;
  /** what applies while a control is blank */
  inherited: UploadLimits;
  /** what a blank accept list means here: the server's whole allowlist, or the form's setting */
  inheritLabel: string;
  /** 'fs' on Settings (fs-* tokens), 'fb' in the builder panel (fb-* classes) */
  variant: 'fs' | 'fb';
  idPrefix: string;
}

export default function UploadLimitsFields({ value, onChange, inherited, inheritLabel, variant, idPrefix }: UploadLimitsFieldsProps) {
  const [typed, setTyped] = useState('');
  const [rejected, setRejected] = useState('');
  const cls = variant === 'fs'
    ? { field: 'fs-field', input: '', hint: 'hint', row: 'fs-row tight' }
    : { field: 'fb-field', input: 'fb-input', hint: 'fb-hint', row: 'fb-pair' };
  const preset = useMemo(() => presetOf(value.accept), [value.accept]);
  const patch = (x: Partial<UploadLimitsValue>) => onChange({ ...value, ...x });

  const choosePreset = (p: Preset) => {
    setRejected('');
    if (p === 'inherit') patch({ accept: [] });
    else if (p === 'documents') patch({ accept: [...ACCEPT_PRESETS.documents] });
    else if (p === 'images') patch({ accept: [...ACCEPT_PRESETS.images] });
    else patch({ accept: value.accept.length ? value.accept : ['pdf'] });
  };

  /** Turn what was typed into chips; say which ones the server would not store. */
  const commitTyped = (raw: string) => {
    const parts = raw.split(/[\s,;]+/).map((s) => s.trim().toLowerCase().replace(/^\./, '')).filter(Boolean);
    if (!parts.length) return;
    const ok = normalizeAccept(parts);
    const bad = parts.filter((p) => !DEFAULT_ACCEPT.includes(p));
    setRejected(bad.length ? `${bad.join(', ')}: not a type the server stores.` : '');
    const next = [...value.accept];
    for (const e of ok) if (!next.includes(e)) next.push(e);
    if (next.length !== value.accept.length) patch({ accept: next });
    setTyped('');
  };

  const removeExt = (ext: string) => { patch({ accept: value.accept.filter((e) => e !== ext) }); setRejected(''); };

  const inheritedTypes = inherited.accept.length === DEFAULT_ACCEPT.length
    ? 'any common document, image or archive'
    : inherited.accept.map((e) => `.${e}`).join(', ');

  return (
    <>
      <div className={cls.row}>
        <div className={cls.field}>
          <label htmlFor={`${idPrefix}-max-files`}>Files per field</label>
          <input
            id={`${idPrefix}-max-files`} className={cls.input} type="number" min={1} max={MAX_FILES_CEILING} step={1}
            value={value.maxFiles} placeholder={String(inherited.maxFiles)}
            onChange={(e) => patch({ maxFiles: e.target.value })}
          />
          <div className={cls.hint}>Blank = {inherited.maxFiles}. Up to {MAX_FILES_CEILING}.</div>
        </div>
        <div className={cls.field}>
          <label htmlFor={`${idPrefix}-max-mb`}>Max size per file (MB)</label>
          <input
            id={`${idPrefix}-max-mb`} className={cls.input} type="number" min={1} max={MAX_SIZE_MB_CEILING} step={1}
            value={value.maxSizeMb} placeholder={String(inherited.maxSizeMb)}
            onChange={(e) => patch({ maxSizeMb: e.target.value })}
          />
          <div className={cls.hint}>Blank = {inherited.maxSizeMb} MB. The server stores up to {MAX_SIZE_MB_CEILING} MB.</div>
        </div>
      </div>
      <div className={cls.field}>
        <label htmlFor={`${idPrefix}-accept`}>Accepted file types</label>
        <select id={`${idPrefix}-accept`} className={cls.input} value={preset} onChange={(e) => choosePreset(e.target.value as Preset)}>
          <option value="inherit">{inheritLabel}</option>
          <option value="documents">Documents (pdf, doc, xls, ppt, csv, txt…)</option>
          <option value="images">Images (png, jpg, gif, webp, heic…)</option>
          <option value="custom">Custom list…</option>
        </select>
        {preset !== 'inherit' && (
          <div className="fs-chips" aria-label="Accepted extensions">
            {value.accept.map((ext) => (
              <span key={ext} className="fs-chip">
                .{ext}
                <button type="button" aria-label={`Remove .${ext}`} onClick={() => removeExt(ext)}>×</button>
              </span>
            ))}
            <input
              className="fs-chip-input" value={typed} placeholder="add: pdf, png…" aria-label="Add an extension"
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commitTyped(typed); } }}
              onBlur={() => commitTyped(typed)}
            />
          </div>
        )}
        {rejected
          ? <div className={cls.hint} style={{ color: '#c53030' }}>{rejected}</div>
          : preset === 'inherit'
            ? <div className={cls.hint}>Blank = {inheritedTypes}.</div>
            : <div className={cls.hint}>Executables and web files (html, svg, js) are never stored, whatever the list says.</div>}
      </div>
    </>
  );
}
